import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, toast } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  formatNumber,
  REPLENISHMENT_POLICIES,
  refusalMessage,
  STOCKING_SOURCES,
  StockingPointClient,
  type PartDTO,
  type ReplenishmentPolicy,
  type StockingPointDTO,
  type StockingSource,
} from "../../api/manufacturing";
import { WarehouseClient } from "../../api/product";
import { policyLabel } from "../purchasing/replenishment/MaterialStory";

type Translate = (key: string, fallback: string) => string;

export function stockingSourceLabel(source: StockingSource, t: Translate): string {
  switch (source) {
    case "BUY":
      return t("stocking.source.buy", "Bought");
    case "MAKE":
      return t("stocking.source.make", "Made");
    case "TRANSFER":
      return t("stocking.source.transfer", "Transferred");
  }
}

const optionalNumber = (value: string): number | null => (value.trim() === "" ? null : Number(value));

/**
 * Where the part is kept, and how each warehouse is replenished: bought, made
 * on runs of its own, or moved from another of its points; the policy, safety
 * days and a fixed minimum and maximum over the part's and its group's. One
 * point is where orders draw it. No row for a warehouse: the part is not kept
 * there, whatever its balance says.
 */
export function PartStockedAtTab({ part, readOnly }: { part: PartDTO; readOnly: boolean }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const client = useMemo(() => new StockingPointClient(http), [http]);
  const key = ["manufacturing", "stocking-point", "part", part.id];
  const points = useQuery({ queryKey: key, queryFn: () => client.forPart(part.id) });
  const warehouses = useQuery({
    queryKey: ["warehouse", "all"],
    queryFn: () => new WarehouseClient(http).findAll(),
    staleTime: 30 * 60_000,
  });
  // Never a distribution warehouse: that stock is Nexera's.
  const ours = (warehouses.data ?? []).filter((warehouse) => warehouse.kind !== "DISTRIBUTION" && warehouse.is_active);
  const warehouseOptions = ours.map((warehouse) => ({ value: String(warehouse.id), label: warehouse.code }));
  const rows = points.data ?? [];

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: key });
    await queryClient.invalidateQueries({ queryKey: ["manufacturing", "part"] });
  };

  const cols = useMemo(() => {
    const c = defineColumns<StockingPointDTO>();
    return [
      c.custom(
        "warehouse",
        (row) => row.warehouse_code ?? "",
        (row) => (
          <span className="flex items-center gap-2">
            <span className="font-medium">{row.warehouse_code}</span>
            {row.is_consumption_point && (
              <Badge variant="accent" size="sm">
                {t("stocking.consumption", "Used from here")}
              </Badge>
            )}
          </span>
        ),
        { header: t("stocking.column.warehouse", "Warehouse"), priority: 1 },
      ),
      c.text("bin", (row) => row.bin ?? "", { header: t("stocking.column.bin", "Bin"), priority: 2 }),
      c.text("source", (row) => stockingSourceLabel(row.source, t), { header: t("stocking.column.source", "Replenished"), priority: 1 }),
      c.text("transfer_from", (row) => row.transfer_from_warehouse_code ?? "", {
        header: t("stocking.column.transfer-from", "Moved from"),
        priority: 3,
      }),
      c.text("policy", (row) => (row.replenishment_policy ? policyLabel(row.replenishment_policy, t) : t("stocking.as-part", "As the part")), {
        header: t("stocking.column.policy", "Planning"),
        priority: 2,
      }),
      c.text("safety_days", (row) => (row.safety_days == null ? "" : String(row.safety_days)), {
        header: t("stocking.column.safety-days", "Safety days"),
        priority: 3,
      }),
      c.text("fixed_minimum", (row) => (row.fixed_minimum == null ? "" : formatNumber(row.fixed_minimum)), {
        header: t("stocking.column.minimum", "Minimum"),
        priority: 3,
      }),
      c.text("fixed_maximum", (row) => (row.fixed_maximum == null ? "" : formatNumber(row.fixed_maximum)), {
        header: t("stocking.column.maximum", "Maximum"),
        priority: 3,
      }),
      c.text("quantity", (row) => (row.quantity == null ? "0" : formatNumber(row.quantity)), {
        header: t("stocking.column.on-hand", "On hand"),
        priority: 2,
      }),
      c.custom(
        "is_consumption_point",
        (row) => (row.is_consumption_point ? 1 : 0),
        (row) => (row.is_consumption_point ? <span>{t("common.yes", "Yes")}</span> : <span />),
        { header: t("stocking.column.consumption", "Used from here"), priority: 2 },
      ),
      c.text("note", (row) => row.note ?? "", { headerKey: "column.note", priority: 4 }),
    ];
  }, [t]);

  return (
    <>
      <p className="border-b border-line px-4 py-2 text-[12px] text-ink-muted lg:px-6">
        {t(
          "stocking.intro",
          "Where this part is kept, and how each place is replenished. Planning, counts and consumption go by these; a warehouse without a row does not keep the part.",
        )}
      </p>
      <DataTable<StockingPointDTO>
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={points.isLoading}
        emptyMessage={t("stocking.none", "Kept nowhere yet.")}
        editor={
          readOnly
            ? undefined
            : {
                fields: {
                  warehouse: {
                    kind: "select",
                    get: (row) => (row.warehouse_id ? String(row.warehouse_id) : ""),
                    set: (row, value) => ({ ...row, warehouse_id: Number(value) }),
                    // A warehouse keeps the part once: the others' are not offered.
                    options: (row) =>
                      warehouseOptions.filter(
                        (option) => Number(option.value) === row.warehouse_id || !rows.some((other) => other.warehouse_id === Number(option.value)),
                      ),
                    required: true,
                  },
                  bin: {
                    kind: "text",
                    get: (row) => row.bin ?? "",
                    set: (row, value) => ({ ...row, bin: value.trim() === "" ? null : value }),
                  },
                  source: {
                    kind: "select",
                    get: (row) => row.source,
                    set: (row, value) => ({
                      ...row,
                      source: value as StockingSource,
                      transfer_from_warehouse_id: value === "TRANSFER" ? row.transfer_from_warehouse_id : null,
                    }),
                    options: () => STOCKING_SOURCES.map((source) => ({ value: source, label: stockingSourceLabel(source, t) })),
                    required: true,
                  },
                  transfer_from: {
                    kind: "select",
                    get: (row) => (row.transfer_from_warehouse_id == null ? "" : String(row.transfer_from_warehouse_id)),
                    set: (row, value) => ({ ...row, transfer_from_warehouse_id: value === "" ? null : Number(value) }),
                    options: (row) => [
                      { value: "", label: t("stocking.transfer-from.none", "Not by transfer") },
                      ...warehouseOptions.filter((option) => Number(option.value) !== row.warehouse_id),
                    ],
                  },
                  policy: {
                    kind: "select",
                    get: (row) => row.replenishment_policy ?? "",
                    set: (row, value) => ({ ...row, replenishment_policy: value === "" ? null : (value as ReplenishmentPolicy) }),
                    options: () => [
                      { value: "", label: t("stocking.as-part", "As the part") },
                      ...REPLENISHMENT_POLICIES.map((policy) => ({ value: policy, label: policyLabel(policy, t) })),
                    ],
                  },
                  safety_days: {
                    kind: "number",
                    get: (row) => (row.safety_days == null ? "" : row.safety_days),
                    set: (row, value) => ({ ...row, safety_days: optionalNumber(value) }),
                  },
                  fixed_minimum: {
                    kind: "number",
                    get: (row) => (row.fixed_minimum == null ? "" : row.fixed_minimum),
                    set: (row, value) => ({ ...row, fixed_minimum: optionalNumber(value) }),
                  },
                  fixed_maximum: {
                    kind: "number",
                    get: (row) => (row.fixed_maximum == null ? "" : row.fixed_maximum),
                    set: (row, value) => ({ ...row, fixed_maximum: optionalNumber(value) }),
                  },
                  is_consumption_point: {
                    kind: "checkbox",
                    get: (row) => row.is_consumption_point,
                    set: (row, value) => ({ ...row, is_consumption_point: value }),
                  },
                  note: {
                    kind: "text",
                    get: (row) => row.note ?? "",
                    set: (row, value) => ({ ...row, note: value.trim() === "" ? null : value }),
                  },
                },
                validate: (row) => {
                  const errors: Record<string, string> = {};
                  if (!row.warehouse_id) errors.warehouse = t("stocking.error.warehouse", "Pick the warehouse.");
                  if (row.source === "TRANSFER" && row.transfer_from_warehouse_id == null) {
                    errors.transfer_from = t("stocking.error.transfer-from", "Say where it is moved from.");
                  }
                  if (row.safety_days != null && !(row.safety_days >= 0)) errors.safety_days = t("validation.not-negative", "Zero or more");
                  if (row.fixed_minimum != null && row.fixed_maximum != null && row.fixed_minimum > row.fixed_maximum) {
                    errors.fixed_minimum = t("stocking.error.min-max", "The minimum cannot be above the maximum.");
                  }
                  return Object.keys(errors).length === 0 ? null : errors;
                },
                newRow: (): StockingPointDTO => ({
                  id: 0,
                  part_id: part.id,
                  warehouse_id: 0,
                  source: part.type === "MANUFACTURED" ? "MAKE" : "BUY",
                  transfer_from_warehouse_id: null,
                  replenishment_policy: null,
                  safety_days: null,
                  fixed_minimum: null,
                  fixed_maximum: null,
                  is_consumption_point: rows.length === 0,
                  bin: null,
                  note: null,
                }),
                addLabel: t("stocking.add", "Keep it at another warehouse"),
                onSave: async (row, isNew) => {
                  try {
                    if (isNew) await client.create(row);
                    else await client.update(row);
                    await refresh();
                  } catch (failure) {
                    toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
                    throw failure;
                  }
                },
                onDelete: async (row) => {
                  await client.remove(row.id);
                  await refresh();
                },
                deleteConfirm: (row) => ({
                  title: t("stocking.delete", "Stop keeping it at {{warehouse}}?", { warehouse: row.warehouse_code ?? "" }),
                }),
              }
        }
      />
    </>
  );
}
